import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { TConsentRequested } from 'services/auth/types'

import { TAuthorizationCtx } from './types'

const DEFAULT_VALUE: TAuthorizationCtx = {
  authorizationId: undefined,
  setAuthorizationId: () => {
    throw new Error('Not Implemented')
  },
  redirectUri: undefined,
  setRedirectUri: () => {
    throw new Error('Not Implemented')
  },
  consentRequested: undefined,
  setConsentRequested: () => {
    throw new Error('Not Implemented')
  },
  requestedClaims: undefined,
  setRequestedClaims: () => {
    throw new Error('Not Implemented')
  },
  requestedScopes: undefined,
  setRequestedScopes: () => {
    throw new Error('Not Implemented')
  },
}

const AuthorizationContext = createContext<TAuthorizationCtx>(DEFAULT_VALUE)

export const useAuthorizationContext = () => useContext(AuthorizationContext)

export const withAuthorizationContext =
  (Component: React.FC) => (props: Record<string, unknown>) => {
    const [authorizationId, setAuthorizationIdState] = useState<string | undefined>(
      () => sessionStorage.getItem('authorizationId') ?? undefined,
    )

    const [redirectUri, setRedirectUriState] = useState<string | undefined>(
      () => sessionStorage.getItem('redirectUri') ?? undefined,
    )

    const [consentRequested, setConsentRequestedState] = useState<TConsentRequested | undefined>(
      () => {
        const stored = sessionStorage.getItem('consentRequested')
        return stored ? JSON.parse(stored) : undefined
      },
    )

    const [requestedClaims, setRequestedClaimsState] = useState<string[] | undefined>(() => {
      const stored = sessionStorage.getItem('requestedClaims')
      return stored ? JSON.parse(stored) : undefined
    })

    const [requestedScopes, setRequestedScopesState] = useState<string[] | undefined>(() => {
      const stored = sessionStorage.getItem('requestedScopes')
      return stored ? JSON.parse(stored) : undefined
    })

    const setAuthorizationId = useCallback((id: string) => {
      if (id) {
        setAuthorizationIdState(id)
        sessionStorage.setItem('authorizationId', id)
      } else {
        setAuthorizationIdState(undefined)
        sessionStorage.removeItem('authorizationId')
      }
    }, [])

    const setRedirectUri = useCallback((uri: string) => {
      if (uri) {
        setRedirectUriState(uri)
        sessionStorage.setItem('redirectUri', uri)
      } else {
        setRedirectUriState(undefined)
        sessionStorage.removeItem('redirectUri')
      }
    }, [])

    const setConsentRequested = useCallback((consent: TConsentRequested | undefined) => {
      if (consent) {
        setConsentRequestedState(consent)
        sessionStorage.setItem('consentRequested', JSON.stringify(consent))
      } else {
        setConsentRequestedState(undefined)
        sessionStorage.removeItem('consentRequested')
      }
    }, [])

    const setRequestedClaims = useCallback((claims: string[] | undefined) => {
      if (claims) {
        setRequestedClaimsState(claims)
        sessionStorage.setItem('requestedClaims', JSON.stringify(claims))
      } else {
        setRequestedClaimsState(undefined)
        sessionStorage.removeItem('requestedClaims')
      }
    }, [])

    const setRequestedScopes = useCallback((scopes: string[] | undefined) => {
      if (scopes) {
        setRequestedScopesState(scopes)
        sessionStorage.setItem('requestedScopes', JSON.stringify(scopes))
      } else {
        setRequestedScopesState(undefined)
        sessionStorage.removeItem('requestedScopes')
      }
    }, [])

    const value = useMemo(
      () => ({
        authorizationId,
        setAuthorizationId,
        redirectUri,
        setRedirectUri,
        consentRequested,
        setConsentRequested,
        requestedClaims,
        setRequestedClaims,
        requestedScopes,
        setRequestedScopes,
      }),
      [
        authorizationId,
        setAuthorizationId,
        redirectUri,
        setRedirectUri,
        consentRequested,
        setConsentRequested,
        requestedClaims,
        setRequestedClaims,
        requestedScopes,
        setRequestedScopes,
      ],
    )

    return (
      <AuthorizationContext.Provider value={value}>
        <Component {...props} />
      </AuthorizationContext.Provider>
    )
  }
